'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCurrency } from '@/hooks/useCurrency';
import styles from './CurrencySwitcher.module.css';

interface CurrencySwitcherProps {
    // 'dropdown' for the desktop header, 'pills' for the mobile menu
    variant?: 'dropdown' | 'pills';
}

export default function CurrencySwitcher({ variant = 'dropdown' }: CurrencySwitcherProps) {
    const { currency, currencies, setCurrency } = useCurrency();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    // A single-currency store doesn't need a switcher
    if (currencies.length < 2) return null;

    if (variant === 'pills') {
        return (
            <div className={styles.pills} role="group" aria-label="Currency">
                {currencies.map((c) => (
                    <button
                        key={c.code}
                        className={`${styles.pill} ${c.code === currency.code ? styles.pillActive : ''}`}
                        onClick={() => setCurrency(c.code)}
                        aria-pressed={c.code === currency.code}
                    >
                        {c.code}
                    </button>
                ))}
            </div>
        );
    }

    return (
        <div className={styles.root} ref={rootRef}>
            <button
                className={styles.trigger}
                onClick={() => setOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label="Change currency"
            >
                {currency.code}
                <ChevronDown size={14} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.ul
                        className={styles.menu}
                        role="listbox"
                        aria-label="Currencies"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                    >
                        {currencies.map((c) => (
                            <li key={c.code}>
                                <button
                                    role="option"
                                    aria-selected={c.code === currency.code}
                                    className={`${styles.option} ${c.code === currency.code ? styles.optionActive : ''}`}
                                    onClick={() => {
                                        setCurrency(c.code);
                                        setOpen(false);
                                    }}
                                >
                                    <span className={styles.optionCode}>{c.code}</span>
                                    <span className={styles.optionName}>{c.name}</span>
                                </button>
                            </li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
}
